import { httpsCallable } from 'firebase/functions';
import { Connection, Transaction, PublicKey } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { anonymousAuthReady, auth, functions } from '../firebaseConfig';
import { openPublishSessionForWallet } from './auth';
const callable = httpsCallable(functions, 'adMarketplace');
const GENESIS_HASHES = {
    'mainnet-beta': '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d',
    devnet: 'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG',
};
let adConnection = null;
let networkCheck = null;
const inFlight = new Map();
const sent = new Set();
let sessionId = '';
export async function authenticateAdWallet(wallet) {
    const uid = wallet?.publicKey?.toBase58?.();
    if (!uid) throw new Error('Connect your wallet first.');
    const token = auth.currentUser?.uid === uid ? await auth.currentUser.getIdTokenResult() : null;
    if (token?.claims?.walletVerified !== true) await openPublishSessionForWallet(wallet);
    if (auth.currentUser?.uid !== uid || wallet.publicKey?.toBase58?.() !== uid) throw new Error('Wallet changed. Please retry.');
    return uid;
}
export async function callAdMarketplace(action, payload = {}, wallet = null) {
    const uid = wallet ? await authenticateAdWallet(wallet) : null;
    await anonymousAuthReady;
    if (uid && (auth.currentUser?.uid !== uid || wallet.publicKey?.toBase58?.() !== uid)) throw new Error('Wallet changed. Please retry.');
    return (await callable({ ...payload, action })).data;
}
export async function executeAdTransaction(wallet, prepared) {
    if (!prepared.transaction) return;
    const uid = await authenticateAdWallet(wallet);
    if (prepared.cluster !== 'devnet' && !(prepared.cluster === 'mainnet-beta' && process.env.REACT_APP_AD_ESCROW_ALLOW_MAINNET === 'true')) throw new Error('Mainnet ad funding is disabled.');
    const transaction = Transaction.from(Buffer.from(prepared.transaction, 'base64'));
    if (transaction.feePayer?.toBase58() !== uid || transaction.signatures.some(entry => entry.publicKey.toBase58() !== uid)) throw new Error('Unexpected transaction signer.');
    if (!process.env.REACT_APP_AD_ESCROW_PROGRAM_ID || prepared.programId !== process.env.REACT_APP_AD_ESCROW_PROGRAM_ID) throw new Error('Escrow transactions are unavailable for this app configuration.');
    const permitted = [prepared.programId, 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'];
    if (!prepared.programId || transaction.instructions.some(instruction => !permitted.includes(instruction.programId.toBase58()))) throw new Error('Unexpected escrow instruction.');
    const endpoint = process.env.REACT_APP_RPC_ENDPOINT;
    if (!endpoint) throw new Error('Configure the app RPC before using ad escrow.');
    if (adConnection?.rpcEndpoint !== endpoint) {
        adConnection = new Connection(endpoint, 'finalized');
        networkCheck = null;
    }
    const connection = adConnection;
    if (!networkCheck) networkCheck = connection.getGenesisHash().catch(error => { networkCheck = null; throw error; });
    if (await networkCheck !== GENESIS_HASHES[prepared.cluster]) throw new Error('The app RPC does not match this escrow transaction network.');
    if (!new PublicKey(uid).equals(wallet.publicKey)) throw new Error('Wallet changed.');
    const signature = await wallet.sendTransaction(transaction, connection);
    const confirmation = await connection.confirmTransaction({ signature, blockhash: prepared.blockhash, lastValidBlockHeight: prepared.lastValidBlockHeight }, 'finalized');
    if (confirmation.value.err) throw new Error('Escrow transaction failed. Funds were not allocated.');
    return signature;
}
function adSession() {
    if (sessionId) return sessionId;
    try { sessionId = window.sessionStorage.getItem('muulti-ad-session') || ''; } catch {}
    if (!/^[a-f0-9]{32}$/.test(sessionId)) {
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        sessionId = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
        try { window.sessionStorage.setItem('muulti-ad-session', sessionId); } catch {}
    }
    return sessionId;
}
export async function recordAdEvent(placement, eventType) {
    const key = [adSession(), placement.id, placement.revision, eventType].join(':');
    try { if (window.sessionStorage.getItem(key)) return; } catch {}
    if (sent.has(key)) return;
    if (inFlight.has(key)) return inFlight.get(key);
    const pending = callAdMarketplace('recordEvent', { placementId: placement.id, eventType, sessionId: adSession() }).then(() => {
        sent.add(key);
        try { window.sessionStorage.setItem(key, '1'); } catch {}
    }).finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
    return pending;
}
